import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSupabase } from '../contexts/SupabaseContext';
import { useBusiness } from '../contexts/BusinessContext';
import {
  buildProposalRequiredActions,
  type ProposalRequiredActionRow,
} from '../lib/proposals/requiredActions';
import { createBusinessCache } from '../lib/controlCentre/actionsCache';

const proposalsCache = createBusinessCache<ProposalRequiredActionRow[]>({
  persistKey: 'proposals',
});

/** Approved proposals waiting to be converted to a booking. */
export function useProposalRequiredActions(enabled = true) {
  const supabase = useSupabase();
  const { selectedBusiness } = useBusiness();
  const businessId = selectedBusiness?.id ?? '';

  const [proposals, setProposals] = useState<ProposalRequiredActionRow[]>(
    () => proposalsCache.get(businessId) ?? []
  );
  const [isLoading, setIsLoading] = useState(() => !proposalsCache.has(businessId));
  const [error, setError] = useState<string | null>(null);

  const fetchProposals = useCallback(async () => {
    if (!businessId || !supabase) return;

    const { data: proposalsData, error: proposalsError } = await supabase
      .from('proposals')
      .select('id, title, status, customer_id, updated_at, created_at, proposal_number, pipeline_opportunities_id')
      .eq('business_id', businessId)
      .eq('status', 'approved')
      .order('updated_at', { ascending: false })
      .limit(100);

    if (proposalsError) throw proposalsError;

    if (!proposalsData?.length) {
      setProposals([]);
      proposalsCache.set(businessId, []);
      return;
    }

    const customerIds = [...new Set(proposalsData.map((p) => p.customer_id).filter(Boolean))];
    const { data: customersData, error: customersError } =
      customerIds.length > 0
        ? await supabase.from('customers').select('id, first_name, last_name').in('id', customerIds)
        : { data: [], error: null };

    if (customersError) throw customersError;

    const customerMap = new Map<string, string>();
    (customersData ?? []).forEach((customer) => {
      const name = `${customer.first_name || ''} ${customer.last_name || ''}`.trim();
      customerMap.set(customer.id, name || 'Unknown contact');
    });

    const proposalIds = proposalsData.map((p) => p.id);
    const { data: depositPayments, error: depositsError } = await supabase
      .from('payments')
      .select('proposal_id, amount, base_amount, status, payment_type')
      .in('proposal_id', proposalIds)
      .eq('status', 'completed')
      .eq('payment_type', 'deposit');

    if (depositsError) throw depositsError;

    const depositByProposal = new Map<string, number>();
    for (const payment of depositPayments ?? []) {
      if (!payment.proposal_id) continue;
      const amount = Number(payment.base_amount ?? payment.amount ?? 0);
      if (!Number.isFinite(amount) || amount <= 0) continue;
      depositByProposal.set(
        payment.proposal_id,
        (depositByProposal.get(payment.proposal_id) ?? 0) + amount
      );
    }

    const mapped = proposalsData.map((proposal) => ({
      ...proposal,
      customer_name: customerMap.get(proposal.customer_id) || 'Unknown contact',
      deposit_amount: depositByProposal.get(proposal.id) ?? 0,
    }));
    setProposals(mapped);
    proposalsCache.set(businessId, mapped);
  }, [businessId, supabase]);

  const load = useCallback(async () => {
    if (!enabled || !businessId || !supabase) {
      setProposals([]);
      setIsLoading(false);
      return;
    }

    const hasCache = proposalsCache.has(businessId);
    if (hasCache) {
      setProposals(proposalsCache.get(businessId) ?? []);
      setIsLoading(false);
    } else {
      setIsLoading(true);
    }
    setError(null);
    try {
      await fetchProposals();
    } catch (err) {
      console.error('Error loading proposal required actions:', err);
      setError('Failed to load proposal actions.');
    } finally {
      setIsLoading(false);
    }
  }, [enabled, businessId, supabase, fetchProposals]);

  useEffect(() => {
    if (!enabled) {
      setIsLoading(false);
      setProposals([]);
      return;
    }
    void load();
  }, [load, enabled]);

  const refreshDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (!enabled || !businessId || !supabase || isLoading) return;

    const channel = supabase
      .channel(`proposal-required-actions-${businessId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'proposals',
          filter: `business_id=eq.${businessId}`,
        },
        () => {
          if (refreshDebounceRef.current) clearTimeout(refreshDebounceRef.current);
          refreshDebounceRef.current = setTimeout(() => {
            void fetchProposals();
          }, 400);
        }
      )
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'payments',
        },
        (payload) => {
          const row = (payload.new ?? payload.old) as { proposal_id?: string | null } | null;
          if (!row?.proposal_id) return;
          if (refreshDebounceRef.current) clearTimeout(refreshDebounceRef.current);
          refreshDebounceRef.current = setTimeout(() => {
            void fetchProposals();
          }, 400);
        }
      )
      .subscribe();

    return () => {
      if (refreshDebounceRef.current) clearTimeout(refreshDebounceRef.current);
      void supabase.removeChannel(channel);
    };
  }, [enabled, businessId, supabase, isLoading, fetchProposals]);

  const proposalActions = useMemo(() => buildProposalRequiredActions(proposals), [proposals]);

  return {
    proposals,
    proposalActions,
    isLoading,
    error,
    refresh: load,
  };
}
